import { useAuthentication } from '@connected/api-client/hooks/use-authentication';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@connected/ui/components/dropdown-menu';
import { createFileRoute, Link, Outlet, redirect, useMatchRoute, useParams } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SubmenuNavigation } from '@/components/submenu-navigation';

export const Route = createFileRoute('/_auth/dashboard/organizations/$organizationId/settings')(
    {
        component: RouteComponent,
        beforeLoad: ({ context, params }) => {
            const hasCorrectRole = context.currentUser?.Roles?.some(
                role => role?.Name === 'Applicatie beheerder' || role?.Name === 'Elektra manager' || role?.Name === 'Account eigenaar',
            );
            if (!context.currentUser?.IsAdmin && !hasCorrectRole) {
                throw redirect({
                    to: '/dashboard/organizations/$organizationId/requests',
                    params: { organizationId: params.organizationId },
                });
            }
        },
    },
);

function RouteComponent() {
    const { t } = useTranslation('dashboard');
    const matchRoute = useMatchRoute();
    const params = useParams({ from: '/_auth/dashboard/organizations/$organizationId/settings' });

    const context = useAuthentication();

    const hideSubmenuForTemplate = matchRoute({ from: '/dashboard/organizations/$organizationId/settings/templates/$templateId' });
    const hideSubmenuForCalibration = matchRoute({ from: '/dashboard/organizations/$organizationId/settings/measuring-instruments/$measurementDeviceId/calibrations' });
    const hideSubmenuForScope12 = matchRoute({ from: '/dashboard/organizations/$organizationId/settings/scope12/$scopeName' });

    const menuItems = [
        ...(context?.currentUser?.IsAdmin
            ? [
                    {
                        label: t('dashboard.settings.inspection.title'),
                        to: '/dashboard/organizations/$organizationId/settings/inspection' as const,
                        disabled: false,
                    },
                    {
                        label: t('dashboard.settings.quotation.title'),
                        to: undefined,
                        disabled: true,
                    },
                    {
                        label: t('dashboard.settings.reports.title'),
                        to: '/dashboard/organizations/$organizationId/settings/templates' as const,
                        disabled: false,
                    },
                ]
            : []),
        {
            label: t('dashboard.settings.standards.title'),
            to: '/dashboard/organizations/$organizationId/settings/norms' as const,
            disabled: false,
        },
        {
            label: t('dashboard.settings.measuring-instruments.title'),
            to: '/dashboard/organizations/$organizationId/settings/measuring-instruments' as const,
            disabled: false,
        },
        {
            label: t('dashboard.settings.remarks.title'),
            to: '/dashboard/organizations/$organizationId/settings/observations' as const,
            disabled: false,
        },
        {
            label: t('dashboard.settings.remarks.recommendations'),
            to: '/dashboard/organizations/$organizationId/settings/recommendations' as const,
            disabled: false,
        },
        {
            label: t('dashboard.settings.checklist.title'),
            to: '/dashboard/organizations/$organizationId/settings/visual-inspections' as const,
            disabled: false,
        },
        {
            label: t('dashboard.settings.scope12.title'),
            to: '/dashboard/organizations/$organizationId/settings/scope12' as const,
            disabled: false,
        },
    ];

    const currentMenuItem = menuItems.find(item =>
        item.to && matchRoute({ to: item.to, params: { organizationId: params.organizationId } }),
    );

    return (
        <>
            {(!hideSubmenuForTemplate && !hideSubmenuForCalibration && !hideSubmenuForScope12) && (
                <div className="mb-6">
                    <div className="flex max-sm:flex-wrap gap-1 md:gap-4 justify-start">
                        <h1 className="text-2xl font-semibold">
                            {t('dashboard.settings.title')}
                        </h1>
                    </div>
                </div>
            )}
            {(!hideSubmenuForTemplate && !hideSubmenuForCalibration && !hideSubmenuForScope12) && (
                <>
                    <div className="sm:hidden mb-4">
                        <DropdownMenu>
                            <DropdownMenuTrigger className="flex items-center gap-2 border-2 border-primary/60 px-3 rounded-lg h-10 bg-background-primary text-primary hover:bg-primary/10 justify-center w-full">
                                <span>{currentMenuItem?.label || t('dashboard.settings.title')}</span>
                                <ChevronDown className="w-5 h-5" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="sm:hidden flex flex-col w-[calc(100vw-2rem)]" align="start">
                                {menuItems
                                    .filter(item => !item.disabled && item.to !== undefined)
                                    .map(item => (
                                        <DropdownMenuItem
                                            key={item.to}
                                            className="p-3 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer text-base"
                                            asChild
                                        >
                                            <Link
                                                to={item.to!}
                                                params={{ organizationId: params.organizationId }}
                                            >
                                                {item.label}
                                            </Link>
                                        </DropdownMenuItem>
                                    ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>

                    <div className="hidden sm:block">
                        <SubmenuNavigation items={menuItems ?? []} />
                    </div>
                </>
            )}
            <Outlet />
        </>
    );
}
